<template>
    <PublicProfileLayout>
        <div class="pricing-page-wrapper overflow-x-hidden relative min-h-screen pb-8 px-3 md:px-8"
            :style="{ '--bg-image': `url(${bgUrl})` }">
            <div class="background-frame"></div>

            <section class="pricing-section relative z-1 py-8 px-2 sm:px-4 md:px-6 flex flex-column align-items-center">
                <div class="text-center mb-5 mt-4">
                    <h1 class="text-900 font-bold text-4xl mb-2">Find the right plan for your needs</h1>
                    <p class="text-600">you can upgrade or downgrade anytime you want</p>
                </div>

                <!-- Tab switcher — tabs for other account types are locked when logged in -->
                <div class="flex bg-white border-round-lg p-1 shadow-1 mb-3">
                    <button v-for="tab in tabs" :key="tab" @click="!isTabLocked(tab) && (activeTab = tab)"
                        class="px-5 py-2 border-none border-round-lg font-semibold transition-colors text-base" :class="[
                            activeTab === tab
                                ? 'bg-primary-custom text-white'
                                : isTabLocked(tab)
                                    ? 'bg-transparent text-400 cursor-not-allowed'
                                    : 'bg-transparent text-black hover:surface-100 cursor-pointer'
                        ]" :title="isTabLocked(tab) ? `Sign in as a ${tab} to view these plans` : ''">
                        {{ tab }}
                    </button>
                </div>

                <!-- hint if logged in -->
                <p v-if="isLoggedIn && userTab" class="text-500 text-sm mb-4 text-center m-0">
                    You are signed in as a <strong>{{ userTab }}</strong>.
                    Use the Upgrade / Downgrade buttons below to change your plan.
                </p>

                <!-- Loading state -->
                <div v-if="packagesLoading" class="flex align-items-center justify-content-center py-8 gap-3">
                    <ProgressSpinner style="width: 40px; height: 40px" />
                    <span class="text-600">Loading packages...</span>
                </div>

                <!-- Plans -->
                <div v-else class="w-full max-w-7xl">
                    <transition name="fade" mode="out-in">
                        <component :is="activeComponent" :mode="displayMode" :currentPlanId="currentPlanId"
                            :featureDefinitions="featuresByTab[activeTab]" :packages="activeTabPackages"
                            :disableActions="managedByInstitute && activeTab === userTab"
                            @package-action="handlePackageAction" @contact-sales="handleContactSales" />
                    </transition>
                </div>
            </section>
        </div>

        <!-- Plan change dialog -->
        <Dialog v-model:visible="showSidebar" modal :closable="!actionLoading" :header="sidebarHeader"
            :style="{ width: 'min(600px, 95vw)' }" @hide="onSidebarHide">
            <div class="flex justify-content-center py-2 px-2">

                <!-- Upgrade confirm -->
                <component v-if="drawerView === 'upgradeConfirm'" :is="confirmComponents.upgrade"
                    :targetPackage="pendingPackage" :nextBillingDate="nextBillingDate" :loading="actionLoading"
                    :suggestedGradeKey="suggestedGradeKey" @confirm="onUpgradeConfirm" @cancel="closeSidebar" />

                <!-- Downgrade confirm -->
                <component v-else-if="drawerView === 'downgradeConfirm'" :is="confirmComponents.downgrade"
                    :currentPlanName="currentPlanName" :targetPackage="pendingPackage"
                    :nextBillingDate="nextBillingDate" :loading="actionLoading"
                    @confirm="handleConfirmChange('downgrade')" @cancel="closeSidebar" />

                <!-- Downgrade success -->
                <component v-else-if="drawerView === 'downgradeSuccess'" :is="confirmComponents.success"
                    :nextBillingDate="nextBillingDate" :targetPackageName="pendingPackage?.name"
                    @continue="closeSidebar" />

                <!-- Processing spinner (PayHere submit or polling) -->
                <div v-else-if="drawerView === 'processing'"
                    class="flex flex-column align-items-center py-8 gap-4 w-full">
                    <ProgressSpinner style="width: 50px; height: 50px" />
                    <p class="text-600 text-center m-0">{{ progressMessage }}</p>
                </div>

            </div>
        </Dialog>

        <!-- Billing details dialog (shown when no card on file) -->
        <BillingDetailsDialog v-model="showBillingDialog" :context="billingContext" :selected-package="pendingPackage"
            :initial-values="billingInitialValues"
            :grade-pricing="pendingPackage?.pricingType === 'grade_based' ? pendingPackage?.priceBreakdown : null"
            :preselected-grade="pendingGradeObject" @submit="submitBilling" />

    </PublicProfileLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import PublicProfileLayout from "@/layout/PublicProfileLayout.vue";
import { usePublicUrl } from "@/utils/getPublicUrl";
import { useAuthStore } from "@/stores/authStore";
import { storeToRefs } from 'pinia';
import { notifyInfo, notifyContrast, notifyError } from "@/utils/notification";
import { notifyIfManagedByInstitute } from "@/utils/subscriptionGuards";
import { backendApi } from '@/api/axios';
import { PACKAGES_ENDPOINTS, PAYMENT_ENDPOINTS } from '@/api/endpoints';
import subscriptionApi from '@/api/subscription';
import payments from '@/api/payments';
import { updateProfile as updateStudentProfile } from '@/api/student';
import { updateProfile as updateTutorProfile } from '@/api/tutor';
import { updateInstitute } from '@/api/institutes';
import ProgressSpinner from 'primevue/progressspinner';
import Dialog from 'primevue/dialog';

import StudentPlan from '@/components/subscriptionPlans/studentSubscriptionPlans.vue';
import TutorPlan from '@/components/subscriptionPlans/tutorSubscriptionPlans.vue';
import InstitutionPlan from '@/components/subscriptionPlans/instituteSubscriptionPlans.vue';

import BillingDetailsDialog from '@/components/payments/BillingDetailsForm.vue';

import StudentUpgradeConfirm   from '@/components/settings/student/subscription/UpgradeConfirm.vue';
import StudentDowngradeConfirm from '@/components/settings/student/subscription/DowngradeConfirm.vue';
import StudentDowngradeSuccess from '@/components/settings/student/subscription/DowngradeSuccess.vue';

import TutorUpgradeConfirm     from '@/components/settings/tutor/subscription/UpgradeConfirm.vue';
import TutorDowngradeConfirm   from '@/components/settings/tutor/subscription/DowngradeConfirm.vue';
import TutorDowngradeSuccess   from '@/components/settings/tutor/subscription/DowngradeSuccess.vue';

import InstituteUpgradeConfirm   from '@/components/settings/institute/subscription/UpgradeConfirm.vue';
import InstituteDowngradeConfirm from '@/components/settings/institute/subscription/DowngradeConfirm.vue';
import InstituteDowngradeSuccess from '@/components/settings/institute/subscription/DowngradeSuccess.vue';

// Constants
const PENDING_KEY = 'pricingPagePendingPackage';

// Router and Store Setup
const router = useRouter();
const authStore = useAuthStore();
const { user, userType } = storeToRefs(authStore);

// Page State Variables
const bgUrl = usePublicUrl("public/how-it-works.jpg");
const tabs = ['Student', 'Tutor', 'Institution'];
const activeTab = ref('Student');

const isLoggedIn = computed(() => !!user.value);
const roleToTab = { student: 'Student', tutor: 'Tutor', institute: 'Institution' };

const packagesLoading = ref(true);
const packagesByTab = ref({ Student: [], Tutor: [], Institution: [] });

// Subscription state
const currentPlanId = ref(null);
const pendingChange = ref(null);
const nextBillingDate = ref('');
const suggestedGradeKey = ref(null);  // Pre-selection for student grade-based plans

// Dialog state
const showSidebar = ref(false);
const drawerView = ref('upgradeConfirm');   // Options: upgradeConfirm, downgradeConfirm, downgradeSuccess, processing
const actionLoading = ref(false);
const progressMessage = ref('');
const pendingPackage = ref(null);
const pendingAction = ref('upgrade');
const pendingGradeKey = ref(null);   // Grade key string
const pendingGradeObject = ref(null);   // Full grade object for billing form pre-fill

// Billing dialog state
const showBillingDialog = ref(false);
const billingContext = ref('paid_subscribe');

// Computed Properties
// Tab that matches the logged-in user's account type
const userTab = computed(() =>
    (isLoggedIn.value && userType.value) ? (roleToTab[userType.value] ?? null) : null
);

// A tab is locked when the user is logged in and it isn't their account type
const isTabLocked = (tab) => isLoggedIn.value && tab !== userTab.value;

const managedByInstitute = computed(() => user.value?.createdBy === 'institute');

const displayMode = computed(() => isLoggedIn.value ? 'settings' : 'onboarding');

const activeComponent = computed(() => {
    if (activeTab.value === 'Student') return StudentPlan;
    if (activeTab.value === 'Tutor') return TutorPlan;
    return InstitutionPlan;
});

// For the user's own tab: annotate packages with current/pending status
const activeTabPackages = computed(() => {
    const pkgs = packagesByTab.value[activeTab.value] || [];
    if (!isLoggedIn.value || activeTab.value !== userTab.value) return pkgs;

    return pkgs.map(pkg => {
        const isCurrent = !!currentPlanId.value && String(currentPlanId.value) === String(pkg.id);
        const isPending = pendingChange.value?.packageId === pkg.id;

        if (managedByInstitute.value) {
            return {
                ...pkg,
                disabled: true,
                actionLabel: isCurrent ? 'Provided by your institute' : 'Unavailable',
                isCurrent,
                isPending: false,
            };
        }

        let actionLabel;
        if (isCurrent) actionLabel = 'Current Plan';
        else if (isPending) actionLabel = `Activates on ${formatDate(pendingChange.value.activationDate)}`;

        return { ...pkg, disabled: isCurrent || isPending, actionLabel, isCurrent, isPending };
    });
});

const currentPlanName = computed(() => {
    const pkgs = packagesByTab.value[userTab.value] || [];
    return pkgs.find(p => String(p.id) === String(currentPlanId.value))?.name ?? '';
});

const billingInitialValues = computed(() => {
    const u = authStore.user;
    if (!u) return {};
    return {
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        phone: u.mobileNumber || '',
        address: u.address || '',
        city: u.city || '',
    };
});

// Pick the right set of confirm/success components based on user's account type
const confirmComponents = computed(() => {
    if (userType.value === 'student') return {
        upgrade: StudentUpgradeConfirm,
        downgrade: StudentDowngradeConfirm,
        success: StudentDowngradeSuccess,
    };
    if (userType.value === 'tutor') return {
        upgrade: TutorUpgradeConfirm,
        downgrade: TutorDowngradeConfirm,
        success: TutorDowngradeSuccess,
    };
    return {
        upgrade: InstituteUpgradeConfirm,
        downgrade: InstituteDowngradeConfirm,
        success: InstituteDowngradeSuccess,
    };
});

const sidebarHeader = computed(() => {
    if (drawerView.value === 'upgradeConfirm') return 'Upgrade Plan';
    if (drawerView.value === 'downgradeConfirm') return 'Downgrade Plan';
    if (drawerView.value === 'downgradeSuccess') return 'Plan Change Scheduled';
    return 'Processing...';
});

// Feature Definitions

const featuresByTab = {
    Student: [
        { key: 'sessions', label: 'Maximum tutors' },
        { key: 'notes', label: 'Maximum notes' },
        { key: 'payments', label: 'Secure payments' },
        { key: 'chat', label: 'In app chat' },
        { key: 'pastPapers', label: 'Past paper access' },
        { key: 'aiFeatures', label: 'AI Study Assistant', info: 'Get access to our AI learning assistant' },
    ],
    Tutor: [
        { key: 'listedSubjects', label: 'Listed subjects' },
        { key: 'enrolledStudents', label: 'Enrolled students' },
        { key: 'notes', label: 'Notes' },
        { key: 'inAppChat', label: 'In app chat' },
        { key: 'securePayments', label: 'Secure payments' },
        { key: 'aiFeatures', label: 'AI features', info: 'Get access to our AI learning assistant' },
        { key: 'pastPapers', label: 'Past papers' },
        { key: 'sessions', label: 'Sessions via platform (per month)' },
    ],
    Institution: [
        { key: 'maxTutors', label: 'Maximum tutors' },
        { key: 'maxStudents', label: 'Maximum students' },
        { key: 'securePayments', label: 'Secure payments' },
        { key: 'aiFeatures', label: 'AI features', info: 'AI assistance capabilities' },
        { key: 'pastPapers', label: 'Past papers' },
        { key: 'inAppChat', label: 'In app chat' },
    ],
};

// Utility Functions

function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function mapStringFeature(value) {
    if (value === null || value === false || value === 'none') return false;
    // 'all' means the feature is fully included — show a tick rather than the word "All".
    if (value === true || value === 'all') return true;
    if (typeof value === 'string') return value.charAt(0).toUpperCase() + value.slice(1);
    return value;
}

function mapLimitValue(value) {
    return value === null || value === undefined ? 'Unlimited' : value.toString();
}

// Package Mapping Functions

function mapStudentPackage(pkg) {
    const isGradeBased = pkg.pricing.type === 'grade_based';
    const isFree = pkg.pricing.type === 'free';
    return {
        id: pkg._id,
        name: pkg.name,
        priceLabel: isGradeBased ? 'Based on Grade' : (isFree ? '0' : pkg.pricing.amount.toString()),
        popular: pkg.isPopular,
        pricingType: pkg.pricing.type,
        subtitle: isFree ? 'Free for all Students' : undefined,
        priceBreakdown: isGradeBased && pkg.pricing.gradePricing?.length
            ? pkg.pricing.gradePricing.map(g => ({ gradeKey: g.gradeKey, label: g.label, amount: g.amount, price: g.amount.toString() }))
            : undefined,
        features: {
            sessions: mapLimitValue(pkg.limits?.bookSessionsMaxTutors),
            notes: mapLimitValue(pkg.limits?.maximumNotes),
            payments: pkg.features?.securePayments ?? true,
            chat: pkg.features?.inAppChat ?? true,
            pastPapers: pkg.features?.pastPaperAccess ?? false,
            aiFeatures: mapStringFeature(pkg.features?.aiFeatures ?? false),
        },
    };
}

function mapTutorPackage(pkg) {
    return {
        id: pkg._id,
        name: pkg.name,
        priceLabel: pkg.pricing.amount === 0 ? '0' : pkg.pricing.amount.toString(),
        popular: pkg.isPopular,
        pricingType: pkg.pricing.type,
        features: {
            listedSubjects: mapLimitValue(pkg.limits.listedSubjects),
            enrolledStudents: mapLimitValue(pkg.limits.enrolledStudents),
            notes: mapLimitValue(pkg.limits.notes),
            sessions: mapLimitValue(pkg.limits.sessionsPerMonth),
            securePayments: pkg.features.securePayments,
            pastPapers: pkg.features.pastPapers,
            inAppChat: pkg.features.inAppChat,
            aiFeatures: mapStringFeature(pkg.features.aiFeatures),
        },
    };
}

function mapInstitutePackage(pkg) {
    return {
        id: pkg._id,
        name: pkg.name,
        priceLabel: pkg.pricing.amount.toString(),
        popular: pkg.isPopular,
        pricingType: pkg.pricing.type,
        features: {
            maxTutors: mapLimitValue(pkg.limits?.maxTutors),
            maxStudents: mapLimitValue(pkg.limits?.maxStudents),
            securePayments: pkg.features?.securePayments ?? true,
            aiFeatures: mapStringFeature(pkg.features?.aiFeatures ?? false),
            pastPapers: pkg.features?.pastPapers ?? false,
            inAppChat: pkg.features?.inAppChat ?? false,
        },
    };
}

// Sidebar Control Helpers

function closeSidebar() {
    if (!actionLoading.value) showSidebar.value = false;
}

function onSidebarHide() {
    drawerView.value = 'upgradeConfirm';
    pendingGradeKey.value = null;
    pendingGradeObject.value = null;
}

// Payment and Subscription Helpers

async function checkHasToken() {
    const res = await payments.getMyToken();
    return res?.data?.data?.hasToken === true;
}

function redirectToPayHere(paymentData, preapprove = false) {
    const isSandbox = paymentData.sandbox === 'true' || paymentData.sandbox === true;
    const url = preapprove
        ? (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_PREAPPROVE_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_PREAPPROVE_URL)
        : (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_URL);
    payments.processPayHereRedirect({ ...paymentData, action: url }, {});
}

async function waitForSubscriptionUpdate(targetPackageId, maxAttempts = 15, intervalMs = 2000) {
    for (let i = 0; i < maxAttempts; i++) {
        try {
            const subRes = await subscriptionApi.getMy();
            const data = subRes?.data?.data ?? {};
            pendingChange.value = data.pendingChange ?? null;
            const sub = data.subscription;
            const isNowActive = sub?.status === 'active' && String(sub.packageId?._id ?? sub.packageId) === String(targetPackageId);
            const isNowPending = pendingChange.value?.packageId === targetPackageId;
            if (isNowActive || isNowPending) return { sub, pending: pendingChange.value };
        } catch { /* Ignore transient errors */ }
        await new Promise(r => setTimeout(r, intervalMs));
    }
    return null;
}

// Core Package Change Handler

async function doChangePackage(packageId, billingData, gradeKey = null) {
    showSidebar.value = true;
    drawerView.value = 'processing';
    progressMessage.value = currentPlanId.value ? 'Processing your plan change...' : 'Setting up your subscription...';

    try {
        const payload = { packageId, ...(billingData || {}) };
        const resolvedGradeKey = gradeKey || billingData?.selectedGradeKey || null;
        if (resolvedGradeKey) payload.selectedGradeKey = resolvedGradeKey;

        const res = currentPlanId.value
            ? await subscriptionApi.changePackage(payload)
            : await subscriptionApi.subscribe(payload);
        const data = res?.data?.data;

        // Redirect to PayHere checkout for paid plans without a saved card
        if (data?.requiresCheckout) {
            showSidebar.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'paid', ts: Date.now() }));
            redirectToPayHere(data.checkoutData, true);
            return;
        }

        // Redirect to PayHere for card tokenization (LKR 30 charge) for free plans without a saved card
        if (data?.requiresTokenization) {
            showSidebar.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, pricingType: 'free', ts: Date.now() }));
            redirectToPayHere(data.tokenizationData, true);
            return;
        }

        progressMessage.value = 'Waiting for subscription to update...';
        const finalResult = await waitForSubscriptionUpdate(packageId);

        if (!finalResult) {
            showSidebar.value = false;
            notifyError('Timeout', 'Plan update is taking longer than expected. Please refresh and try again.');
            return;
        }

        const { sub: finalSub, pending: finalPending } = finalResult;

        if (finalSub?.status === 'active') {
            currentPlanId.value = String(finalSub.packageId?._id ?? finalSub.packageId);
            nextBillingDate.value = formatDate(finalSub.nextBillingDate);
        }

        if (pendingAction.value === 'downgrade') {
            drawerView.value = 'downgradeSuccess';
        } else {
            showSidebar.value = false;
            if (finalPending) {
                notifyContrast('Scheduled', `Your plan change to ${finalPending.packageName} is scheduled for ${formatDate(finalPending.activationDate)}`);
            } else {
                notifyContrast('Success', `You're now on the ${pendingPackage.value.name} plan!`);
            }
        }
    } catch (err) {
        showSidebar.value = false;
        if (notifyIfManagedByInstitute(err)) return;
        const msg = err?.response?.data?.message || '';
        notifyError('Error', msg || 'Failed to change plan. Please try again.');
    }
}

// User Action Handlers

const handlePackageAction = ({ action, package: pkg }) => {
    if (!isLoggedIn.value) {
        notifyInfo('Login required', 'Please log in or sign up to select a plan.');
        router.push({ path: '/login', query: { redirect: '/pricing' } });
        return;
    }
    if (action !== 'upgrade' && action !== 'downgrade') return;

    pendingPackage.value = pkg;
    drawerView.value = action === 'upgrade' ? 'upgradeConfirm' : 'downgradeConfirm';
    showSidebar.value = true;
};

// Student UpgradeConfirm emits the selected grade object
const onUpgradeConfirm = async (selectedGrade) => {
    pendingGradeKey.value = selectedGrade?.gradeKey || null;
    pendingGradeObject.value = selectedGrade || null;
    await handleConfirmChange('upgrade');
};

async function handleConfirmChange(action) {
    pendingAction.value = action;
    actionLoading.value = true;
    try {
        const hasToken = await checkHasToken();
        if (hasToken) {
            await doChangePackage(pendingPackage.value.id, null, pendingGradeKey.value);
        } else {
            billingContext.value = pendingPackage.value.pricingType === 'free' ? 'free_tokenize' : 'paid_subscribe';
            showBillingDialog.value = true;
        }
    } catch {
        notifyError('Error', 'Something went wrong. Please try again.');
    } finally {
        actionLoading.value = false;
    }
}

async function submitBilling(formData) {
    showBillingDialog.value = false;

    // Save billing details to user profile 
    try {
        const profilePayload = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            mobileNumber: formData.phone,
            address: formData.address,
            city: formData.city,
        };
        if (userType.value === 'student') await updateStudentProfile(profilePayload);
        else if (userType.value === 'tutor') await updateTutorProfile(profilePayload);
        else await updateInstitute(profilePayload);

        if (authStore.user) Object.assign(authStore.user, {
            firstName: formData.firstName, lastName: formData.lastName,
            mobileNumber: formData.phone, address: formData.address, city: formData.city,
        });
    } catch { /* Ignore error; subscription will still proceed */ }

    await doChangePackage(pendingPackage.value.id, formData, pendingGradeKey.value);
}

const handleContactSales = () => {
    notifyContrast('Coming Soon', 'Please reach out to our sales team for custom plans.');
};

// PayHere Return Handler

async function checkPendingChange() {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;

    let pending;
    try { pending = JSON.parse(raw); }
    catch { sessionStorage.removeItem(PENDING_KEY); return; }

    if (!pending?.packageId) { sessionStorage.removeItem(PENDING_KEY); return; }

    const pkg = (packagesByTab.value[userTab.value] || []).find(p => p.id === pending.packageId);
    if (pkg) pendingPackage.value = pkg;

    showSidebar.value = true;
    drawerView.value = 'processing';
    progressMessage.value = 'Activating your subscription, please wait...';

    const activeResult = await waitForSubscriptionUpdate(pending.packageId);
    sessionStorage.removeItem(PENDING_KEY);

    if (activeResult) {
        const { sub, pending: pChange } = activeResult;
        if (sub?.status === 'active') {
            currentPlanId.value = String(sub.packageId?._id ?? sub.packageId);
            nextBillingDate.value = formatDate(sub.nextBillingDate);
        }
        showSidebar.value = false;
        if (pChange) {
            notifyContrast('Scheduled', `Your plan change to ${pChange.packageName} is scheduled for ${formatDate(pChange.activationDate)}`);
        } else {
            notifyContrast('Success', 'Your subscription has been updated!');
        }
    } else {
        showSidebar.value = false;
        notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
    }
}

// Lifecycle Hooks

onMounted(async () => {
    packagesLoading.value = true;
    try {
        const [studentRes, tutorRes, instituteRes] = await Promise.all([
            backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'student' } }),
            backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'tutor' } }),
            backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'institute' } }),
        ]);
        packagesByTab.value.Student = (studentRes.data?.data || []).map(mapStudentPackage);
        packagesByTab.value.Tutor = (tutorRes.data?.data || []).map(mapTutorPackage);
        packagesByTab.value.Institution = (instituteRes.data?.data || []).map(mapInstitutePackage);

        if (isLoggedIn.value && userTab.value) {
            activeTab.value = userTab.value;
            try {
                const subRes = await subscriptionApi.getMy();
                const data = subRes?.data?.data ?? {};
                pendingChange.value = data.pendingChange ?? null;
                suggestedGradeKey.value = data.suggestedGradeKey ?? null;
                const sub = data.subscription;
                if (sub?.status === 'active') {
                    currentPlanId.value = String(sub.packageId?._id ?? sub.packageId);
                    nextBillingDate.value = formatDate(sub.nextBillingDate);
                }
            } catch { /* No active subscription found */ }

            await checkPendingChange();
        }
    } catch { /* Handle package load failure silently */ } finally {
        packagesLoading.value = false;
    }
});
</script>

<style scoped>
.background-frame {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    pointer-events: none;
}

.background-frame::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-image: v-bind('`url(${bgUrl})`');
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    opacity: 0.35;
}

.bg-primary-custom {
    background-color: #1FC9A3;
    color: white;
}

.bg-primary-custom:hover {
    background-color: #1eaa8c;
}

/* Landing page overrides Button colors in subscription plans */
:deep(.custom-brown-btn),
:deep(.custom-teal-btn),
:deep(.p-button-danger) {
    background-color: #1FC9A3 !important;
    border: 1px solid #1FC9A3 !important;
    color: white !important;
    font-weight: 600;
}

:deep(.custom-brown-btn:hover),
:deep(.custom-teal-btn:hover),
:deep(.p-button-danger:hover) {
    background-color: #1eaa8c !important;
    border-color: #1eaa8c !important;
}

:deep(.p-button-outlined) {
    background-color: var(--accent-50) !important;
    color: black !important;
    font-weight: 600;
}

:deep(.disclaimer-heading) {
    color: #1FC9A3 !important;
}
</style>
